import { t } from 'i18next';
import { useMutation, useQuery } from 'react-query';

import { AttributeStatus } from 'companySettings/enums';
import {
  comparePartFinderAttributeVersions,
  getPartFinderAttributes,
  getPartFinderAttributeVersions,
  publishPartFinderAttributes,
  removePartFinderAttributes,
  savePartFinderAttribute,
} from 'companySettings/services/partFinderAttribute';
import { IPartFinderAttribute, IPartFinderAttributePayload } from 'companySettings/types';
import { NotificationPosition } from 'core/enums';
import { useHandleErrors, useInfiniteQuery } from 'core/hooks';

const PART_FINDER_ATTRIBUTES_QUERY_KEY = 'partFinderAttributes';
const PART_FINDER_ATTRIBUTE_VERSIONS_QUERY_KEY = 'partFinderAttributeVersions';

export const useGetPartFinderAttributes = () =>
  useInfiniteQuery<IPartFinderAttribute>(
    getPartFinderAttributes,
    PART_FINDER_ATTRIBUTES_QUERY_KEY,
    {},
    { fetchAllPages: true }
  );

export const useGetPartFinderAttributeVersions = (attributeId: number, enabled: boolean) =>
  useQuery(
    [PART_FINDER_ATTRIBUTE_VERSIONS_QUERY_KEY, attributeId],
    ({ signal }) => getPartFinderAttributeVersions(attributeId, signal),
    { enabled }
  );

export const useComparePartFinderAttributeVersions = (
  fromVersionId: number,
  toVersionId: number,
  enabled: boolean
) =>
  useQuery(
    ['partFinderAttributeComparison', fromVersionId, toVersionId],
    ({ signal }) => comparePartFinderAttributeVersions({ fromVersionId, toVersionId }, signal),
    { enabled }
  );

export const usePartFinderAttributeActions = (
  position: NotificationPosition = NotificationPosition.FORM
) => {
  return {
    ...useSavePartFinderAttribute(position),
    ...useRemovePartFinderAttributes(position),
    ...usePublishPartFinderAttributes(position),
  };
};

const useSavePartFinderAttribute = (position: NotificationPosition) => {
  const { errors } = useHandleErrors({
    success: (
      payload: IPartFinderAttributePayload,
      data: { partFinderAttribute: IPartFinderAttribute }
    ) => {
      if (AttributeStatus.PUBLISHED === payload?.status) {
        return { message: t('messagePartFinderAttributePublishSuccess') };
      }

      // A draft saved back to the published state is dropped, so the list is published again.
      if (AttributeStatus.PUBLISHED === data?.partFinderAttribute?.status) {
        return { message: t('messagePartFinderAttributeDraftDiscarded') };
      }

      return { message: t('messagePartFinderAttributeSaveSuccess') };
    },
    loading: () => ({
      message: t('messagePartFinderAttributeSaveInProgress'),
    }),
    error: () => ({
      title: t('errorTitlePartFinderAttributeSave'),
    }),
    position,
    // The grid updates the saved row itself; a publish adds a version to the history.
    invalidateQueries: [PART_FINDER_ATTRIBUTE_VERSIONS_QUERY_KEY],
  });

  const { mutateAsync } = useMutation(savePartFinderAttribute, {
    ...errors,
  });

  return { savePartFinderAttribute: mutateAsync };
};

const useRemovePartFinderAttributes = (position: NotificationPosition) => {
  const { errors } = useHandleErrors({
    success: (ids: number[]) => ({
      message: t('messagePartFinderAttributesRemoveSuccess', { count: ids?.length }),
    }),
    loading: (ids: number[]) => ({
      message: t('messagePartFinderAttributesRemoveInProgress', { count: ids?.length }),
    }),
    error: (ids: number[]) => ({
      title: t('errorTitlePartFinderAttributesRemove', { count: ids?.length }),
    }),
    position,
  });

  const { mutateAsync } = useMutation(removePartFinderAttributes, {
    ...errors,
  });

  return { removePartFinderAttributes: mutateAsync };
};

const usePublishPartFinderAttributes = (position: NotificationPosition) => {
  const { errors } = useHandleErrors({
    // The server leaves out drafts that matched their release, so its count is the exact one.
    success: (ids: number[], response?: { published?: number }) => ({
      message: t('messagePartFinderAttributesPublishSuccess', {
        count: response?.published ?? ids?.length,
      }),
    }),
    loading: (ids: number[]) => ({
      message: t('messagePartFinderAttributesPublishInProgress', { count: ids?.length }),
    }),
    error: (ids: number[]) => ({
      title: t('errorTitlePartFinderAttributesPublish', { count: ids?.length }),
    }),
    position,
    // Statuses and versions change on the rows, and each publish adds a version to the history.
    invalidateQueries: [PART_FINDER_ATTRIBUTES_QUERY_KEY, PART_FINDER_ATTRIBUTE_VERSIONS_QUERY_KEY],
  });

  const { mutateAsync } = useMutation(publishPartFinderAttributes, {
    ...errors,
  });

  return { publishPartFinderAttributes: mutateAsync };
};
